import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Корневой ErrorBoundary — ловит исключения из дочернего React-дерева,
 * чтобы любой throw в компоненте не положил всё SPA.
 *
 * При ошибке логирует stack в console и в /api/client-log (best-effort)
 * и показывает простой UI с возможностью перезагрузить страницу.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error:', error, info)
    try {
      void fetch('/api/client-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          level: 'error',
          message: error.message,
          stack: error.stack,
          componentStack: info.componentStack,
          url: typeof window !== 'undefined' ? window.location.href : '',
        }),
      })
    } catch {
      // network log failure is non-fatal
    }
  }

  handleReload = (): void => {
    if (typeof window !== 'undefined') window.location.reload()
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children
    if (this.props.fallback) return this.props.fallback
    return (
      <div
        style={{
          padding: '2rem',
          maxWidth: 640,
          margin: '4rem auto',
          fontFamily: 'system-ui, sans-serif',
          color: '#1f2937',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
          Что-то пошло не так
        </h1>
        <p style={{ marginBottom: '1rem' }}>
          Приложение столкнулось с непредвиденной ошибкой. Перезагрузите страницу.
          Если ошибка повторяется — обратитесь в поддержку.
        </p>
        {this.state.error?.message && (
          <pre
            style={{
              background: '#f3f4f6',
              padding: '1rem',
              borderRadius: 6,
              overflow: 'auto',
              fontSize: 13,
              marginBottom: '1rem',
            }}
          >
            {this.state.error.message}
          </pre>
        )}
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '0.5rem 1rem',
            background: '#2563eb',
            color: 'white',
            border: 0,
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Перезагрузить
        </button>
      </div>
    )
  }
}
